'use client';

import { Crisp } from 'crisp-sdk-web';
import { useEffect } from 'react';

import { HeaderLink } from '@/src/components/header/HeaderLink/HeaderLink';
import { HeaderLogo } from '@/src/components/header/HeaderLogo/HeaderLogo';
import { HeaderLocales } from '@/src/components/header/HeaderLocales/HeaderLocales';
import { HeaderAccount } from '@/src/components/header/HeaderAccount/HeaderAccount';
import { IResolvedHeader } from '@/src/utils/collections/header.mapper';
import { HeaderDownload } from '@/src/components/header/HeaderDownload/HeaderDownload';
import { HeaderMobile } from '@/src/components/header/HeaderMobile/HeaderMobile';
import { IZoneWithDestinationsAndPromo } from '@/src/modules/destinations/destinations.modules';
import { useHeader } from '@/src/components/header/useHeader';
import { HeaderSearchScroll } from '@/src/components/header/HeaderSearchScroll/HeaderSearchScroll';

type IHeaderProps = {
  header: IResolvedHeader;
  locale: string;
  destinations: Record<string, IZoneWithDestinationsAndPromo>;
};

export const Header = ({ header, locale, destinations }: IHeaderProps) => {
  const { showHeader, open, setOpen, user } = useHeader();

  useEffect(() => {
    Crisp.configure('5cdb3a44-3017-47fe-b577-f64ac152da6c');
    Crisp.load();
  });

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-20 flex h-16 animate-slide-from-top justify-center bg-primary transition-transform duration-500 ease-in-out lg:h-20 ${
        showHeader ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      {!open && (
        <HeaderSearchScroll
          destinations={destinations}
          phrase={header.searchPhrase}
          icon={header.searchIcon}
          placeholder={header.searchPlaceholder}
        />
      )}

      <div className="absolute inset-x-6 top-3 flex w-[1200px] max-w-[calc(100%-48px)] justify-between gap-4 lg:inset-x-auto lg:top-4 lg:px-2 xl:top-2.5">
        <HeaderLogo locale={locale} setOpen={setOpen} />

        <HeaderMobile
          header={header}
          user={user}
          locale={locale}
          open={open}
          setOpen={setOpen}
        />
        <div
          className={
            'hidden p-8 lg:static lg:flex lg:flex-row lg:items-center lg:justify-center lg:p-0'
          }
        >
          <ul className="flex list-none flex-col gap-14 text-nowrap lg:flex-row lg:justify-center lg:gap-6">
            {header.items.map(link => (
              <HeaderLink key={link.url} link={link.url} label={link.name} />
            ))}
          </ul>
        </div>
        <div
          className={
            'hidden flex-col gap-8 p-8 lg:static lg:flex lg:flex-row lg:items-center lg:justify-center lg:gap-2 lg:p-0'
          }
        >
          <HeaderLocales
            locale={locale}
            localeList={header.locales}
            buttonLanguage={header.buttonLanguage}
          />
          <HeaderDownload
            className={'bg-white text-primary'}
            text={header.buttonDownload}
            linkToAppStore={header.linkToAppStore}
            linkToPlayStore={header.linkToPlayStore}
          />
          <HeaderAccount
            signInTranslation={header.buttonSignIn}
            signOutTranslation={header.buttonSignOut}
            user={user}
            locale={locale}
          />
        </div>
      </div>
    </nav>
  );
};
